import React, { useState, useMemo, useCallback } from 'react';
import { Card, Form, Row, Col, Table, Badge, Button, InputGroup } from 'react-bootstrap';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import * as Icon from 'react-feather';

const VEHICLE_DEFAULTS = {
    torton: {
        label: 'Tortón M2 52K',
        rendimiento: 3.5,
        costoUnidad: 1250000,
        anosDepreciacion: 15,
        seguroAnual: 59014.07,
        seguroCajaAnual: 0,
        mantenimientoMensual: 10000,
        gpsMensual: 235,
        celularMensual: 300,
        rentaCajaMensual: 0,
        lavadoMensual: 0,
    },
    tractocamion: {
        label: 'Tractocamión',
        rendimiento: 2.5,
        costoUnidad: 2500000,
        anosDepreciacion: 15,
        seguroAnual: 95000,
        seguroCajaAnual: 15000,
        mantenimientoMensual: 15000,
        gpsMensual: 235,
        celularMensual: 300,
        rentaCajaMensual: 8000,
        lavadoMensual: 500,
    },
};

const DEFAULT_INPUTS = {
    cliente: '',
    origen: '',
    destino: '',
    tipoViaje: 'local',
    vehiculo: 'torton',
    recorridoSencillo: 30,
    recorridoTotal: 80,
    diasViaje: 1,
    diasUtilesSemana: 6,
    diasUtilesMes: 25.8,
    diasUtilesAnio: 312,
    rendimiento: 3.5,
    casetasSencillo: 0,
    vecesRecargaTag: 0,
    comisionRecargaTag: 12,
    precioDiesel: 27,
    viaticosOperador: 0,
    sueldoSemanalOperador: 4000,
    sueldoSemanalOperaciones: 2000,
    impuestoEstatalPct: 3,
    pagoMensualIMSS: 6610.50,
    comisionOperador: 0,
    comisionOperaciones: 0,
    comisionMonitoreo: 0,
    comisionVenta: 3,
    fijoSemanalGasolina: 750,
    bonoSemanalOperador: 1000,
    gastoAnualReclutamiento: 20000,
    costoMensualPension: 1300,
    costoMensualContadoras: 3000,
    costoMensualFumigacion: 500,
    rentaCajaMensual: 0,
    lavadoMensual: 0,
    gpsMensual: 235,
    seguroAnualTracto: 59014.07,
    seguroAnualCaja: 0,
    celularMensual: 300,
    mantenimientoMensual: 10000,
    costoAnualContabilidad: 3000,
    costoUnidad: 1250000,
    anosDepreciacion: 15,
    impuestoEBIT: 30,
    tarifaVenta: 5650,
};

function Calculadora() {
    const [inputs, setInputs] = useState(DEFAULT_INPUTS);
    const [origenValue, setOrigenValue] = useState(null);
    const [destinoValue, setDestinoValue] = useState(null);

    const handleChange = (field, value) => {
        setInputs(prev => ({ ...prev, [field]: value }));
    };

    const handleNumChange = (field, value) => {
        const num = value === '' ? 0 : parseFloat(value);
        handleChange(field, isNaN(num) ? 0 : num);
    };

    const calculateDistance = useCallback((origen, destino) => {
        if (origen && destino && window.google) {
            const service = new window.google.maps.DistanceMatrixService();
            service.getDistanceMatrix(
                {
                    origins: [origen],
                    destinations: [destino],
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    unitSystem: window.google.maps.UnitSystem.METRIC,
                },
                (response, status) => {
                    if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
                        const distanceKm = parseFloat((response.rows[0].elements[0].distance.value / 1000).toFixed(1));
                        setInputs(prev => ({
                            ...prev,
                            recorridoSencillo: distanceKm,
                            recorridoTotal: prev.tipoViaje === 'local' ? distanceKm * 2 : distanceKm,
                        }));
                    }
                }
            );
        }
    }, []);

    const handleOrigenSelect = (value) => {
        setOrigenValue(value);
        const label = value?.label || '';
        setInputs(prev => ({ ...prev, origen: label }));
        if (label && inputs.destino) {
            calculateDistance(label, inputs.destino);
        }
    };

    const handleDestinoSelect = (value) => {
        setDestinoValue(value);
        const label = value?.label || '';
        setInputs(prev => ({ ...prev, destino: label }));
        if (inputs.origen && label) {
            calculateDistance(inputs.origen, label);
        }
    };

    const handleVehicleChange = (vehiculo) => {
        const defaults = VEHICLE_DEFAULTS[vehiculo];
        setInputs(prev => ({
            ...prev,
            vehiculo,
            rendimiento: defaults.rendimiento,
            costoUnidad: defaults.costoUnidad,
            anosDepreciacion: defaults.anosDepreciacion,
            seguroAnualTracto: defaults.seguroAnual,
            seguroAnualCaja: defaults.seguroCajaAnual,
            mantenimientoMensual: defaults.mantenimientoMensual,
            gpsMensual: defaults.gpsMensual,
            celularMensual: defaults.celularMensual,
            rentaCajaMensual: defaults.rentaCajaMensual,
            lavadoMensual: defaults.lavadoMensual,
        }));
    };

    const handleTripTypeChange = (tipoViaje) => {
        if (tipoViaje === 'local') {
            setInputs(prev => ({
                ...prev,
                tipoViaje,
                viaticosOperador: 0,
                casetasSencillo: 0,
                recorridoTotal: prev.recorridoSencillo * 2,
            }));
        } else {
            setInputs(prev => ({
                ...prev,
                tipoViaje,
                viaticosOperador: 500,
                casetasSencillo: 1500,
                recorridoTotal: prev.recorridoSencillo,
            }));
        }
    };

    const costs = useMemo(() => {
        const i = inputs;
        const diasMes = i.diasUtilesMes;
        const diasAnio = i.diasUtilesAnio;
        const diasSemana = i.diasUtilesSemana;

        // Casetas
        const tipoPago = i.tipoViaje === 'local' ? 2 : 1;
        const casetas = i.casetasSencillo * tipoPago;
        const comisionesTag = i.vecesRecargaTag * i.comisionRecargaTag * tipoPago;

        // Combustible
        const diesel = (i.recorridoTotal / i.rendimiento) * i.precioDiesel;

        // Sueldos
        const sueldoFijoOperador = i.sueldoSemanalOperador / diasSemana;
        const sueldoFijoOperaciones = (i.sueldoSemanalOperaciones / diasSemana) + (i.fijoSemanalGasolina / diasSemana);
        const imssOperador = i.pagoMensualIMSS / diasMes;
        const impuestoEstatal = (i.sueldoSemanalOperador / diasSemana) * (i.impuestoEstatalPct / 100);
        const bonoOperador = i.bonoSemanalOperador / diasSemana;
        const gasolinaOps = i.fijoSemanalGasolina / diasSemana;

        // Comisiones variables
        const comisionOperador = i.tarifaVenta * (i.comisionOperador / 100);
        const comisionOperaciones = i.tarifaVenta * (i.comisionOperaciones / 100);
        const comisionMonitoreo = i.tarifaVenta * (i.comisionMonitoreo / 100);
        const comisionVenta = i.tarifaVenta * (i.comisionVenta / 100);

        // RH
        const reclutamiento = i.gastoAnualReclutamiento / diasAnio;

        // Viáticos
        const viaticos = i.viaticosOperador * i.diasViaje;

        // Gastos fijos diarios
        const pension = i.costoMensualPension / diasMes;
        const contadoras = i.costoMensualContadoras / diasMes;
        const fumigacion = i.costoMensualFumigacion / diasMes;
        const rentaCaja = i.rentaCajaMensual / diasMes;
        const lavado = i.lavadoMensual / diasMes;
        const gps = i.gpsMensual / diasMes;
        const seguroTracto = i.seguroAnualTracto / diasAnio;
        const seguroCaja = i.seguroAnualCaja / diasAnio;
        const celular = i.celularMensual / diasMes;
        const mantenimiento = i.mantenimientoMensual / diasMes;
        const contabilidad = i.costoAnualContabilidad / diasAnio;
        const depreciacion = i.costoUnidad / (i.anosDepreciacion * diasAnio);

        const items = [
            { category: 'CASETAS', name: 'Casetas', value: casetas },
            { category: 'CASETAS', name: 'Comisiones TAG', value: comisionesTag },
            { category: 'COMBUSTIBLE', name: 'Diesel', value: diesel },
            { category: 'SUELDO OPERADOR', name: 'Sueldo Fijo Operador', value: sueldoFijoOperador },
            { category: 'SUELDO OPERADOR', name: 'Sueldo Fijo Operaciones', value: sueldoFijoOperaciones },
            { category: 'SUELDO OPERADOR', name: 'IMSS Operador', value: imssOperador },
            { category: 'SUELDO OPERADOR', name: 'Bono Operador', value: bonoOperador },
            { category: 'SUELDO OPERADOR', name: 'Impuesto Estatal Sueldo', value: impuestoEstatal },
            { category: 'SUELDO OPERADOR', name: 'Gasolina Operaciones', value: gasolinaOps },
            { category: 'RH', name: 'Comisiones Reclutamiento', value: reclutamiento },
            { category: 'VIÁTICOS', name: 'Viáticos Operador', value: viaticos },
            { category: 'COMISIONES', name: 'Comisión Operador', value: comisionOperador },
            { category: 'COMISIONES', name: 'Comisión Operaciones', value: comisionOperaciones },
            { category: 'COMISIONES', name: 'Comisión Monitoreo', value: comisionMonitoreo },
            { category: 'COMISIONES', name: 'Comisión Venta', value: comisionVenta },
            { category: 'PENSIÓN', name: 'Pensión', value: pension },
            { category: 'CONTABILIDAD', name: 'Contadoras', value: contadoras },
            { category: 'CONTABILIDAD', name: 'Sistema Contabilidad', value: contabilidad },
            { category: 'FUMIGACIÓN', name: 'Fumigación', value: fumigacion },
            { category: 'RENTA CAJA', name: 'Renta Caja Seca', value: rentaCaja },
            { category: 'LAVADO', name: 'Lavado Tracto', value: lavado },
            { category: 'GPS', name: 'GPS', value: gps },
            { category: 'SEGURO', name: 'Seguro Tracto', value: seguroTracto },
            { category: 'SEGURO', name: 'Seguro Caja Seca', value: seguroCaja },
            { category: 'CELULAR', name: 'Celular Operador', value: celular },
            { category: 'MANTENIMIENTO', name: 'Desgaste Tracto', value: mantenimiento },
            { category: 'DEPRECIACIÓN', name: 'Depreciación Tracto', value: depreciacion },
        ];

        const totalGastos = items.reduce((sum, item) => sum + item.value, 0);
        const ebit = i.tarifaVenta - totalGastos;
        const impuestos = ebit > 0 ? ebit * (i.impuestoEBIT / 100) : 0;
        const depreciacion50 = depreciacion * 0.5;
        const utilidadNeta = ebit - impuestos + depreciacion50;
        const costoPorKm = i.recorridoTotal > 0 ? i.tarifaVenta / i.recorridoTotal : 0;

        return {
            items,
            totalGastos,
            ebit,
            ebitPct: i.tarifaVenta > 0 ? (ebit / i.tarifaVenta) * 100 : 0,
            impuestos,
            depreciacion50,
            utilidadNeta,
            utilidadPct: i.tarifaVenta > 0 ? (utilidadNeta / i.tarifaVenta) * 100 : 0,
            costoPorKm,
        };
    }, [inputs]);

    const fmt = (n) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const pct = (n, total) => total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '0.0%';

    return (
        <div className="col-12">
            {/* Trip & Vehicle Selection */}
            <Card className="mb-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0"><Icon.Sliders size={18} className="me-2" />Configuración del Viaje</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Tipo de Viaje</Form.Label>
                                <Form.Select value={inputs.tipoViaje} onChange={(e) => handleTripTypeChange(e.target.value)}>
                                    <option value="local">Local (Circuito)</option>
                                    <option value="foraneo">Foráneo (Sencillo)</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Tipo de Vehículo</Form.Label>
                                <Form.Select value={inputs.vehiculo} onChange={(e) => handleVehicleChange(e.target.value)}>
                                    <option value="torton">Tortón M2 52K</option>
                                    <option value="tractocamion">Tractocamión</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Cliente</Form.Label>
                                <Form.Control type="text" value={inputs.cliente} onChange={(e) => handleChange('cliente', e.target.value)} placeholder="Nombre del cliente" />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Tarifa de Venta</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>$</InputGroup.Text>
                                    <Form.Control type="number" value={inputs.tarifaVenta} onChange={(e) => handleNumChange('tarifaVenta', e.target.value)} />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Origen</Form.Label>
                                <GooglePlacesAutocomplete
                                    apiKey="AIzaSyD4TV67BkLPPGMbnmcLLFqGKpEQ1OX1Y8I"
                                    selectProps={{
                                        value: origenValue,
                                        onChange: handleOrigenSelect,
                                        placeholder: 'Buscar dirección...',
                                        isClearable: true,
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Destino</Form.Label>
                                <GooglePlacesAutocomplete
                                    apiKey="AIzaSyD4TV67BkLPPGMbnmcLLFqGKpEQ1OX1Y8I"
                                    selectProps={{
                                        value: destinoValue,
                                        onChange: handleDestinoSelect,
                                        placeholder: 'Buscar dirección...',
                                        isClearable: true,
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label>Recorrido Sencillo (km)</Form.Label>
                                <Form.Control type="number" value={inputs.recorridoSencillo} onChange={(e) => handleNumChange('recorridoSencillo', e.target.value)} />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label>Recorrido Total (km)</Form.Label>
                                <Form.Control type="number" value={inputs.recorridoTotal} onChange={(e) => handleNumChange('recorridoTotal', e.target.value)} />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label>Días de Viaje</Form.Label>
                                <Form.Control type="number" value={inputs.diasViaje} onChange={(e) => handleNumChange('diasViaje', e.target.value)} />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Input Parameters */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0"><Icon.Settings size={18} className="me-2" />Parámetros de Costos</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        {/* Column 1: Operación */}
                        <Col md={4}>
                            <h6 className="text-primary border-bottom pb-2 mb-3">Operación</h6>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Rendimiento (km/l)</Form.Label>
                                <Form.Control size="sm" type="number" step="0.1" value={inputs.rendimiento} onChange={(e) => handleNumChange('rendimiento', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Precio Diesel ($/l)</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.precioDiesel} onChange={(e) => handleNumChange('precioDiesel', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Casetas por Viaje Sencillo</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.casetasSencillo} onChange={(e) => handleNumChange('casetasSencillo', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Veces Recarga TAG</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.vecesRecargaTag} onChange={(e) => handleNumChange('vecesRecargaTag', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Comisión Recarga TAG</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.comisionRecargaTag} onChange={(e) => handleNumChange('comisionRecargaTag', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Viáticos Operador / Día</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.viaticosOperador} onChange={(e) => handleNumChange('viaticosOperador', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Días Útiles / Semana</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.diasUtilesSemana} onChange={(e) => handleNumChange('diasUtilesSemana', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Días Útiles / Mes</Form.Label>
                                <Form.Control size="sm" type="number" step="0.1" value={inputs.diasUtilesMes} onChange={(e) => handleNumChange('diasUtilesMes', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Días Útiles / Año</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.diasUtilesAnio} onChange={(e) => handleNumChange('diasUtilesAnio', e.target.value)} />
                            </Form.Group>
                        </Col>

                        {/* Column 2: Sueldos & Comisiones */}
                        <Col md={4}>
                            <h6 className="text-primary border-bottom pb-2 mb-3">Sueldos y Comisiones</h6>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Sueldo Semanal Operador</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.sueldoSemanalOperador} onChange={(e) => handleNumChange('sueldoSemanalOperador', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Sueldo Semanal Operaciones</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.sueldoSemanalOperaciones} onChange={(e) => handleNumChange('sueldoSemanalOperaciones', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">% Impuesto Estatal Sueldo</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.impuestoEstatalPct} onChange={(e) => handleNumChange('impuestoEstatalPct', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Pago Mensual SUA IMSS</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.pagoMensualIMSS} onChange={(e) => handleNumChange('pagoMensualIMSS', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Bono Semanal Operador</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.bonoSemanalOperador} onChange={(e) => handleNumChange('bonoSemanalOperador', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Fijo Semanal Gasolina Ops</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.fijoSemanalGasolina} onChange={(e) => handleNumChange('fijoSemanalGasolina', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">% Comisión Operador</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.comisionOperador} onChange={(e) => handleNumChange('comisionOperador', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">% Comisión Operaciones</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.comisionOperaciones} onChange={(e) => handleNumChange('comisionOperaciones', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">% Comisión Monitoreo</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.comisionMonitoreo} onChange={(e) => handleNumChange('comisionMonitoreo', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">% Comisión Venta</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.comisionVenta} onChange={(e) => handleNumChange('comisionVenta', e.target.value)} />
                            </Form.Group>
                        </Col>

                        {/* Column 3: Gastos Fijos */}
                        <Col md={4}>
                            <h6 className="text-primary border-bottom pb-2 mb-3">Gastos Fijos</h6>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Gasto Anual Reclutamiento</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.gastoAnualReclutamiento} onChange={(e) => handleNumChange('gastoAnualReclutamiento', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Pensión Mensual</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.costoMensualPension} onChange={(e) => handleNumChange('costoMensualPension', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Contadoras Mensual</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.costoMensualContadoras} onChange={(e) => handleNumChange('costoMensualContadoras', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Fumigación Mensual</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.costoMensualFumigacion} onChange={(e) => handleNumChange('costoMensualFumigacion', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Renta Caja Seca Mensual</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.rentaCajaMensual} onChange={(e) => handleNumChange('rentaCajaMensual', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Lavado Tractocamión Mensual</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.lavadoMensual} onChange={(e) => handleNumChange('lavadoMensual', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">GPS Mensual</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.gpsMensual} onChange={(e) => handleNumChange('gpsMensual', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Seguro Anual Tracto</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.seguroAnualTracto} onChange={(e) => handleNumChange('seguroAnualTracto', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Seguro Anual Caja Seca</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.seguroAnualCaja} onChange={(e) => handleNumChange('seguroAnualCaja', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Celular Operador Mensual</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.celularMensual} onChange={(e) => handleNumChange('celularMensual', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Mantenimiento Tracto Mensual</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.mantenimientoMensual} onChange={(e) => handleNumChange('mantenimientoMensual', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Sistema Contabilidad Anual</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.costoAnualContabilidad} onChange={(e) => handleNumChange('costoAnualContabilidad', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Costo Unidad</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.costoUnidad} onChange={(e) => handleNumChange('costoUnidad', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">Años Depreciación</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.anosDepreciacion} onChange={(e) => handleNumChange('anosDepreciacion', e.target.value)} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small mb-0">% Impuesto sobre EBIT</Form.Label>
                                <Form.Control size="sm" type="number" value={inputs.impuestoEBIT} onChange={(e) => handleNumChange('impuestoEBIT', e.target.value)} />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Results */}
            <Row>
                {/* Cost Breakdown Table */}
                <Col md={8}>
                    <Card className="mb-4">
                        <Card.Header className="bg-light">
                            <h5 className="mb-0"><Icon.List size={18} className="me-2" />Desglose de Costos por Viaje</h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table striped hover responsive className="mb-0">
                                <thead className="table-dark">
                                    <tr>
                                        <th>Categoría</th>
                                        <th>Concepto</th>
                                        <th className="text-end">Costo</th>
                                        <th className="text-end">% de Costos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {costs.items.map((item, idx) => (
                                        <tr key={idx} style={{ opacity: item.value === 0 ? 0.4 : 1 }}>
                                            <td><small className="text-muted">{item.category}</small></td>
                                            <td>{item.name}</td>
                                            <td className="text-end">{fmt(item.value)}</td>
                                            <td className="text-end">{pct(item.value, costs.totalGastos)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light fw-bold">
                                    <tr>
                                        <td colSpan={2}>Total Gastos por Viaje</td>
                                        <td className="text-end">{fmt(costs.totalGastos)}</td>
                                        <td className="text-end">100.0%</td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Summary Cards */}
                <Col md={4}>
                    <Card className="mb-3 border-primary">
                        <Card.Body className="text-center">
                            <h6 className="text-muted mb-1">Tarifa de Venta</h6>
                            <h3 className="text-primary mb-1">{fmt(inputs.tarifaVenta)}</h3>
                            <small className="text-muted">{fmt(costs.costoPorKm)} por km</small>
                        </Card.Body>
                    </Card>
                    <Card className="mb-3">
                        <Card.Body className="text-center">
                            <h6 className="text-muted mb-1">Total Gastos por Viaje</h6>
                            <h3 className="text-dark mb-0">{fmt(costs.totalGastos)}</h3>
                        </Card.Body>
                    </Card>
                    <Card className={`mb-3 border-${costs.ebit >= 0 ? 'success' : 'danger'}`}>
                        <Card.Body className="text-center">
                            <h6 className="text-muted mb-1">EBIT por Viaje</h6>
                            <h3 className={`text-${costs.ebit >= 0 ? 'success' : 'danger'} mb-1`}>{fmt(costs.ebit)}</h3>
                            <Badge bg={costs.ebit >= 0 ? 'success' : 'danger'}>{costs.ebitPct.toFixed(1)}% de venta</Badge>
                        </Card.Body>
                    </Card>
                    <Card className="mb-3">
                        <Card.Body className="text-center">
                            <h6 className="text-muted mb-1">Impuestos ({inputs.impuestoEBIT}% de EBIT)</h6>
                            <h3 className="text-warning mb-0">{fmt(costs.impuestos)}</h3>
                        </Card.Body>
                    </Card>
                    <Card className={`mb-3 border-${costs.utilidadNeta >= 0 ? 'success' : 'danger'}`} style={{ borderWidth: '2px' }}>
                        <Card.Body className="text-center">
                            <h6 className="text-muted mb-1">Utilidad Neta por Viaje</h6>
                            <h2 className={`text-${costs.utilidadNeta >= 0 ? 'success' : 'danger'} mb-1`}>{fmt(costs.utilidadNeta)}</h2>
                            <Badge bg={costs.utilidadNeta >= 0 ? 'success' : 'danger'} className="fs-6">{costs.utilidadPct.toFixed(1)}% de venta</Badge>
                        </Card.Body>
                    </Card>
                    <Card className="mb-3 bg-light">
                        <Card.Body>
                            <h6 className="mb-3 text-center">Resumen del Viaje</h6>
                            <div className="d-flex justify-content-between mb-1"><span>Cliente:</span><strong>{inputs.cliente || '—'}</strong></div>
                            <div className="d-flex justify-content-between mb-1"><span>Ruta:</span><strong>{inputs.origen || '—'} → {inputs.destino || '—'}</strong></div>
                            <div className="d-flex justify-content-between mb-1"><span>Tipo:</span><Badge bg="info">{inputs.tipoViaje === 'local' ? 'Local' : 'Foráneo'}</Badge></div>
                            <div className="d-flex justify-content-between mb-1"><span>Vehículo:</span><Badge bg="secondary">{VEHICLE_DEFAULTS[inputs.vehiculo].label}</Badge></div>
                            <div className="d-flex justify-content-between mb-1"><span>Distancia:</span><strong>{inputs.recorridoTotal} km</strong></div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default Calculadora;
